import React, { FC, useMemo, useState } from 'react';
import { Modal, View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { fullWidth, fullHeight, scale } from '@components/frame';
import TouchableElement from '@components/common/touchableElement';
import { font } from '@styles/fonts';
import { NCFileVersionData, VersionSelectionModalProps } from './types';
import { getBoxShadowStyle } from '@styles/shadow';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import i18n from 'i18n-js';
import moment from 'moment';
import getPcsCount from '@components/ncFileVersions/helpers/getPcsCount';
import { sortVersionsBySequence } from '@components/ncFileVersions/helpers/sortVersionsBySequence';

const VersionSelectionModal: FC<VersionSelectionModalProps> = ({
  C,
  visible,
  versions,
  currentVersion,
  otherVersionId,
  onClose,
  onSelect,
  anchorPosition,
  column,
}) => {
  const [showInactive, setShowInactive] = useState(false);

  const inactiveVersionsCount = useMemo(() => {
    return versions.filter((version) => {
      if (version.ncFileVersionId === currentVersion.versionId) {
        return false;
      }
      if (version.ncFileVersionId === otherVersionId) {
        return false;
      }
      return version.isActive === false;
    }).length;
  }, [versions, currentVersion.versionId, otherVersionId]);

  const filteredVersions = useMemo(() => {
    const filtered = versions.filter((version) => {
      if (version.ncFileVersionId === currentVersion.versionId) {
        return false;
      }
      if (version.ncFileVersionId === otherVersionId) {
        return false;
      }
      if (showInactive) {
        return true;
      }
      return version.isActive;
    });

    return sortVersionsBySequence(filtered, false);
  }, [versions, currentVersion.versionId, otherVersionId, showInactive]);

  const showInactiveLabel = showInactive
    ? i18n.t('Hide Inactive Versions')
    : i18n.t('Show Inactive Versions');

  const formatEvent = (value?: string | null) => {
    if (!value) {
      return '--';
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('DD MMM YYYY HH:mm') : value;
  };

  const versionLabelPrefix = i18n.t('Version');
  const firstLabel = i18n.t('First');
  const lastLabel = i18n.t('Last');
  const unknownLabel = i18n.t('Unknown');

  const handleSelect = (version: NCFileVersionData) => {
    onSelect(version);
  };

  const renderVersionRow = (version: NCFileVersionData) => {
    const pcsCount = getPcsCount(version);

    return (
      <TouchableElement key={version.ncFileVersionId} onPress={() => handleSelect(version)}>
        {({ pressHoverColor, boxStyle }) => (
          <View
            style={[
              boxStyle,
              styles.versionRow,
              {
                backgroundColor: C(pressHoverColor('Background/Light/Field')),
              },
            ]}
          >
            <View style={styles.versionHeaderRow}>
              <View style={styles.versionTitle}>
                <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>
                  {versionLabelPrefix} {version.sequenceNumber}
                </Text>
              </View>
              <View style={styles.versionDetailRight}>
                {typeof pcsCount === 'number' && (
                  <View style={styles.pcsBlock}>
                    <Text style={[font.Subheadline, { color: C('Label/Light/Tertiary') }]}>{pcsCount}</Text>
                    <Text style={[font.Caption1, { color: C('Label/Light/Tertiary') }]}>{i18n.t('Pcs')}</Text>
                  </View>
                )}
              </View>
            </View>
            <View style={styles.metadataRow}>
              <View style={styles.metadataColumn}>
                <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{firstLabel}</Text>
                <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{version.firstRanByMachineName || unknownLabel}</Text>
                <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{formatEvent(version.firstRanOn)}</Text>
              </View>
              <View style={[styles.metadataColumn, styles.metadataColumnRight]}>
                <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{lastLabel}</Text>
                <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{version.lastRanByMachineName || unknownLabel}</Text>
                <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{formatEvent(version.lastRanOn)}</Text>
              </View>
            </View>
          </View>
        )}
      </TouchableElement>
    );
  };

  const viewportWidth = fullWidth;
  const viewportHeight = fullHeight;
  const horizontalMargin = scale(24);
  const minDropdownWidth = scale(320);
  const verticalOffset = scale(8);
  const fallbackTop = scale(120);
  const maxDropdownHeight = scale(520);
  const minDropdownHeight = scale(280);

  const dropdownWidth = (() => {
    const desired = Math.max(anchorPosition?.width ?? 0, minDropdownWidth);
    const maxAllowed = Math.max(viewportWidth - horizontalMargin * 2, minDropdownWidth);
    return Math.min(desired, maxAllowed);
  })();

  const dropdownLeft = (() => {
    if (!anchorPosition) {
      return Math.max((viewportWidth - dropdownWidth) / 2, horizontalMargin);
    }
    const alignedLeft = column === 'right' ? anchorPosition.left + anchorPosition.width - dropdownWidth : anchorPosition.left;
    const maxLeft = viewportWidth - dropdownWidth - horizontalMargin;
    return Math.min(Math.max(alignedLeft, horizontalMargin), Math.max(maxLeft, horizontalMargin));
  })();

  const dropdownTop = (() => {
    const desiredTop = anchorPosition ? anchorPosition.top + verticalOffset : fallbackTop;
    if (!viewportHeight) {
      return desiredTop;
    }
    const maxTop = Math.max(viewportHeight - horizontalMargin - minDropdownHeight, horizontalMargin);
    return Math.min(desiredTop, maxTop);
  })();

  const availableHeight = viewportHeight ? Math.max(viewportHeight - dropdownTop - horizontalMargin, 0) : maxDropdownHeight;
  const dropdownMaxHeight = viewportHeight ? Math.min(availableHeight, maxDropdownHeight) : maxDropdownHeight;

  return (
    <Modal transparent animationType="fade" visible={visible} onRequestClose={onClose}>
      <View style={styles.overlay}>
        <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={onClose} />
        <View pointerEvents="box-none" style={styles.dropdownRegion}>
          <View
            style={[
              styles.sheet,
              getBoxShadowStyle({ type: 'Field', C }),
              {
                backgroundColor: C('Background/Light/Field'),
                width: dropdownWidth,
                top: dropdownTop,
                left: dropdownLeft,
                maxHeight: dropdownMaxHeight,
              },
            ]}
          >
            <Text style={[font.Subheadline, styles.sheetTitle, { color: C('Label/Light/Primary') }]}>
              {i18n.t('Select another version to compare')}
            </Text>
            <ScrollView style={styles.list}>
              {filteredVersions.map((version, index) => (
                <React.Fragment key={version.ncFileVersionId}>
                  {index > 0 && (
                    <View
                      style={{
                        height: StyleSheet.hairlineWidth,
                        backgroundColor: C('Hairline/Light/No trans'),
                        marginHorizontal: scale(16),
                      }}
                    />
                  )}
                  {renderVersionRow(version)}
                </React.Fragment>
              ))}
            </ScrollView>
            {inactiveVersionsCount > 0 && (
              <TouchableElement onPress={() => setShowInactive((prev) => !prev)}>
                {({ pressHoverColor, boxStyle }) => (
                  <View
                    style={[
                      boxStyle,
                      styles.toggleButton,
                      {
                        backgroundColor: C(pressHoverColor('Background/Light/Detail')),
                      },
                    ]}
                  >
                    <Text style={[font.Body, { color: C('Link/Light/Primary') }]}>{showInactiveLabel}</Text>
                  </View>
                )}
              </TouchableElement>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.3)',
  },
  dropdownRegion: {
    ...StyleSheet.absoluteFillObject,
  },
  sheet: {
    position: 'absolute',
    borderRadius: scale(SCREEN_BORDER_RADIUS * 2),
    padding: scale(20),
    gap: scale(16),
  },
  sheetTitle: {
    textAlign: 'center',
  },
  list: {
    flexGrow: 0,
  },
  versionRow: {
    borderRadius: scale(12),
    paddingHorizontal: scale(16),
    paddingVertical: scale(12),
    // marginBottom: scale(8),
    // gap: scale(12),
  },
  versionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  versionTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(8),
  },
  versionDetailRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(12),
  },
  pcsBlock: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: scale(4),
  },
  metadataRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: scale(4),
    gap: scale(12),
  },
  metadataColumn: {
    flex: 1,
  },
  metadataColumnRight: {
    alignItems: 'flex-end',
  },
  toggleButton: {
    borderRadius: scale(999),
    paddingVertical: scale(12),
    alignItems: 'center',
  },
});

export default VersionSelectionModal;
